// ════════════════════════════════════════════════════════════════════════════
// AdminMonitorTabV2 — refactor com primitivos Lpa* + LpaChart
//
// vs AdminMonitorTab.jsx (legado):
//   • 4 KPIs via LpaKpiCard (com sparkline + delta)
//   • Charts via LpaChart (consistência + a11y)
//   • LpaPageHeader + LpaSectionHeader
//   • Auto-refresh preservado (60s)
// ════════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LpaPageHeader,
  LpaSectionHeader,
  LpaKpiCard,
  LpaCard,
  LpaBadge,
  LpaButton,
  LpaEmptyState,
} from "../components/ui/lpa";
import LpaChart from "../components/charts/LpaChart";
import { supabase } from "../supabaseClient.js";
import { fmtK } from "./adminUtils.jsx";

interface MetricEntry {
  value: number;
  ts: string;
  meta?: Record<string, unknown>;
}

interface HistoryPoint {
  ts: string;
  health_latency_ms?: number;
  total_aum?: number;
  total_clients?: number;
  health_status?: number;
}

type LatestMap = Record<string, MetricEntry>;

interface ThemeLike {
  card: string;
  text: string;
  muted: string;
  border: string;
  bg: string;
}

interface Props {
  T: ThemeLike;
}

const STATUS_VARIANT: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  healthy: "success",
  degraded: "warning",
  unhealthy: "danger",
};

const STATUS_LABEL: Record<string, string> = {
  healthy: "Saudável",
  degraded: "Degradado",
  unhealthy: "Crítico",
};

function fmtTs(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  })} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function AdminMonitorTabV2({ T: _T }: Props): React.ReactElement {
  const [latest, setLatest] = useState<LatestMap>({});
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: lm, error: lmErr } = await supabase
        .from("latest_metrics")
        .select("metric_name, metric_value, recorded_at, metadata");

      if (lmErr) throw lmErr;

      const map: LatestMap = {};
      (
        (lm ?? []) as Array<{
          metric_name: string;
          metric_value: number;
          recorded_at: string;
          metadata?: Record<string, unknown>;
        }>
      ).forEach((r) => {
        map[r.metric_name] = {
          value: Number(r.metric_value),
          ts: r.recorded_at,
          meta: r.metadata,
        };
      });
      setLatest(map);

      const since = new Date(Date.now() - 7 * 86400_000).toISOString();
      const { data: hist } = await supabase
        .from("system_metrics")
        .select("metric_name, metric_value, recorded_at")
        .in("metric_name", ["health_latency_ms", "total_aum", "total_clients"])
        .gte("recorded_at", since)
        .order("recorded_at", { ascending: true })
        .limit(500);

      const grouped: Record<string, HistoryPoint> = {};
      (
        (hist ?? []) as Array<{
          metric_name: string;
          metric_value: number;
          recorded_at: string;
        }>
      ).forEach((r) => {
        const key = r.recorded_at.slice(0, 16);
        if (!grouped[key]) grouped[key] = { ts: key };
        const point = grouped[key] as unknown as Record<string, number | string>;
        point[r.metric_name] = Number(r.metric_value);
      });
      setHistory(Object.values(grouped));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    intervalRef.current = setInterval(fetchData, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  // Health status atual
  const healthStatusRaw = latest.health_status?.meta?.status as string | undefined;
  const statusKey = healthStatusRaw ?? "neutral";
  const statusVariant = STATUS_VARIANT[statusKey] ?? "neutral";
  const statusLabel = STATUS_LABEL[statusKey] ?? "Sem dados";

  // Latency sparkline data
  const latencySpark = history
    .filter((h) => h.health_latency_ms != null)
    .slice(-20)
    .map((h) => ({ m: h.ts, v: h.health_latency_ms ?? 0 }));

  return (
    <div style={{ padding: 0 }}>
      <LpaPageHeader
        title="Monitoramento do sistema"
        sub="Health · latência · métricas operacionais · auto-refresh 60s"
        actions={
          <>
            <LpaBadge variant={statusVariant} size="md">
              {statusLabel}
            </LpaBadge>
            <LpaButton variant="ghost" onClick={fetchData}>
              ↻ Atualizar
            </LpaButton>
          </>
        }
      />

      {error && (
        <LpaCard variant="elevated" style={{ marginBottom: 16, borderColor: "var(--lpa-danger)" }}>
          <div style={{ color: "var(--lpa-danger)", fontFamily: "var(--lpa-font-mono)" }}>
            ⚠ {error}
          </div>
        </LpaCard>
      )}

      {/* KPIs principais */}
      <LpaSectionHeader title="Métricas principais" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <LpaKpiCard
          label="Latência do health-check"
          value={latest.health_latency_ms ? `${Math.round(latest.health_latency_ms.value)}ms` : "—"}
          valueColor={
            latest.health_latency_ms && latest.health_latency_ms.value > 1000
              ? "var(--lpa-danger)"
              : latest.health_latency_ms && latest.health_latency_ms.value > 500
                ? "var(--lpa-warning)"
                : undefined
          }
          loading={loading}
          hint={
            latest.health_latency_ms
              ? `Atualizado: ${fmtTs(latest.health_latency_ms.ts)}`
              : undefined
          }
        />
        <LpaKpiCard
          label="AUM total"
          value={latest.total_aum ? `R$ ${fmtK(latest.total_aum.value)}` : "—"}
          loading={loading}
          hint={latest.total_aum ? `Atualizado: ${fmtTs(latest.total_aum.ts)}` : undefined}
        />
        <LpaKpiCard
          label="Total de clientes"
          value={latest.total_clients ? Math.round(latest.total_clients.value) : "—"}
          loading={loading}
          hint={latest.total_clients ? `Atualizado: ${fmtTs(latest.total_clients.ts)}` : undefined}
        />
        <LpaKpiCard
          label="Snapshots no DB"
          value={latest.total_snapshots ? Math.round(latest.total_snapshots.value) : "—"}
          loading={loading}
          hint={
            latest.total_snapshots ? `Atualizado: ${fmtTs(latest.total_snapshots.ts)}` : undefined
          }
        />
      </div>

      {/* Charts históricos */}
      <LpaSectionHeader title="Histórico (7 dias)" sub="Latência · AUM · clientes ativos" />
      <div style={{ display: "grid", gap: 16 }}>
        <LpaCard variant="elevated">
          <div
            style={{
              fontFamily: "var(--lpa-font-mono)",
              fontSize: "var(--lpa-text-xs)",
              color: "var(--lpa-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
            }}
          >
            Latência do health-check (ms)
          </div>
          {history.length > 1 ? (
            <LpaChart
              kind="line"
              data={
                history.filter((h) => h.health_latency_ms != null) as unknown as Array<
                  Record<string, unknown>
                >
              }
              series={[{ key: "health_latency_ms", label: "Latência (ms)" }]}
              xKey="ts"
              height={200}
              referenceLines={[
                { value: 1000, label: "Threshold crítico", color: "var(--lpa-danger)" },
              ]}
            />
          ) : (
            <LpaEmptyState variant="no-data" compact />
          )}
        </LpaCard>

        <LpaCard variant="elevated">
          <div
            style={{
              fontFamily: "var(--lpa-font-mono)",
              fontSize: "var(--lpa-text-xs)",
              color: "var(--lpa-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
            }}
          >
            AUM total (R$)
          </div>
          {history.filter((h) => h.total_aum != null).length > 1 ? (
            <LpaChart
              kind="area"
              data={
                history.filter((h) => h.total_aum != null) as unknown as Array<
                  Record<string, unknown>
                >
              }
              series={[{ key: "total_aum", label: "AUM" }]}
              xKey="ts"
              height={200}
            />
          ) : (
            <LpaEmptyState variant="no-data" compact />
          )}
        </LpaCard>
      </div>

      {/* Sparkline (não usada no momento — poderia ser adicionada) */}
      {void latencySpark}
    </div>
  );
}
